import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const compactUsd = (v) => `$${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0)}`;
import { formatCurrency } from "@/lib/format";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, Activity, Loader2 } from "lucide-react";
import BackfillMonths from "@/components/BackfillMonths";

const MONTH_CHOICES = [6, 9, 12];

/** Month-over-month profit + markup so a client sliding backwards is obvious. */
export default function ProfitTrend({ onOpenClient }) {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/profit-report/trend?months=${months}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [months, reload]);

  const series = data?.series || [];
  const clients = data?.clients || [];
  const b = data?.business;

  return (
    <div className="space-y-4" data-testid="profit-trend">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Activity className="w-5 h-5 text-blue-600" /> Profit trend
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">Month over month — is the business, and each client, getting better or worse?</p>
          </div>
          <div className="flex items-center gap-1.5" data-testid="profit-trend-range">
            {MONTH_CHOICES.map((n) => (
              <button key={n} onClick={() => setMonths(n)} data-testid={`profit-trend-months-${n}`}
                className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${months === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                {n} mo
              </button>
            ))}
          </div>
        </div>

        {b && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="profit-trend-summary">
            <Tile label={`Profit · ${b.latest_label}`} value={formatCurrency(b.latest_profit || 0)} />
            {b.previous_label ? (
              <>
                <Tile label={`vs ${b.previous_label}`} value={`${b.delta_profit >= 0 ? "+" : "−"}${formatCurrency(Math.abs(b.delta_profit))}`} dir={b.delta_profit >= 0 ? "up" : "down"} />
                <Tile label="Margin change" value={`${b.delta_margin_pts >= 0 ? "+" : ""}${b.delta_margin_pts} pts`} dir={b.direction} />
                <Tile label="Shipping markup change" value={`${b.delta_markup_pts >= 0 ? "+" : ""}${b.delta_markup_pts} pts`} dir={b.delta_markup_pts >= 0 ? "up" : "down"} />
              </>
            ) : (
              <>
                <Tile label="Margin" value={`${b.latest_margin_pct ?? 0}%`} />
                <Tile label="vs earlier months" value="No comparison yet" />
                <Tile label="Coming next" value="Calculate more months" />
              </>
            )}
          </div>
        )}

        <div className="h-64 mt-5" data-testid="profit-trend-chart">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => compactUsd(v)} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, n) => (n === "Margin %" || n === "Shipping markup %" ? `${v}%` : formatCurrency(v))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area yAxisId="l" type="monotone" dataKey="profit" name="Profit" stroke="#059669" strokeWidth={2.5} fill="url(#profitFill)" connectNulls />
                <Line yAxisId="r" type="monotone" dataKey="margin_pct" name="Margin %" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line yAxisId="r" type="monotone" dataKey="shipping_markup_pct" name="Shipping markup %" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Built from each month&apos;s saved figures — months nobody calculated are left as gaps.
          {series[series.length - 1]?.partial && series[series.length - 1]?.calculated && (
            <> {series[series.length - 1].label} is still in progress, so the headline numbers compare the last two complete months.</>
          )}
        </p>
        <BackfillMonths months={months} onDone={() => setReload((n) => n + 1)} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="profit-trend-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2.5 px-4">Client</th>
              <th className="py-2.5 px-4">Trend</th>
              <th className="py-2.5 px-4 text-right">Margin now</th>
              <th className="py-2.5 px-4 text-right">vs last month</th>
              <th className="py-2.5 px-4 text-right">Markup now</th>
              <th className="py-2.5 px-4 text-right">Markup change</th>
              <th className="py-2.5 px-4 text-right">Profit now</th>
              <th className="py-2.5 px-4 text-right">Profit change</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.account_id} data-testid={`profit-trend-row-${c.account_id}`} onClick={() => onOpenClient?.(c.account_id)}
                className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                <td className="py-2.5 px-4 font-semibold text-slate-900 hover:text-blue-700">{c.company_name}</td>
                <td className="py-2.5 px-4"><Spark points={c.points} /></td>
                <td className="py-2.5 px-4 text-right">{c.latest.margin_pct}%</td>
                <td className="py-2.5 px-4 text-right"><Delta value={c.delta_margin_pts} suffix=" pts" dir={c.direction} na={!c.comparable} /></td>
                <td className="py-2.5 px-4 text-right">{c.latest.markup_pct_effective == null ? <span className="text-slate-400">n/a</span> : `${c.latest.markup_pct_effective}%`}</td>
                <td className="py-2.5 px-4 text-right"><Delta value={c.delta_markup_pts} suffix=" pts" dir={c.delta_markup_pts >= 0 ? "up" : "down"} na={!c.comparable} /></td>
                <td className="py-2.5 px-4 text-right font-bold text-slate-900">{formatCurrency(c.latest.profit)}</td>
                <td className="py-2.5 px-4 text-right"><Delta value={c.delta_profit} money dir={c.delta_profit >= 0 ? "up" : "down"} na={!c.comparable} /></td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-slate-400">{loading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "No calculated months yet — run “Calculate all” on Run the month."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Sorted worst-first: the clients whose margin dropped the most are at the top.</p>
    </div>
  );
}

function Spark({ points = [] }) {
  const vals = points.map((p) => p.margin_pct);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const span = max - min || 1;
  return (
    <span className="inline-flex items-end gap-0.5 h-7">
      {points.map((p, i) => {
        const h = 4 + ((p.margin_pct - min) / span) * 22;
        const last = i === points.length - 1;
        return <span key={p.label} title={`${p.label}: ${p.margin_pct}%`} style={{ height: `${h}px` }}
          className={`w-1.5 rounded-sm ${last ? "bg-emerald-600" : "bg-slate-300"}`} />;
      })}
    </span>
  );
}

function Delta({ value, suffix = "", money = false, dir, na = false }) {
  if (na) return <span className="text-slate-400">no prior month</span>;
  const flat = Math.abs(value) < (money ? 1 : 0.1);
  const Icon = flat ? Minus : dir === "up" ? ArrowUpRight : ArrowDownRight;
  const cls = flat ? "text-slate-400" : dir === "up" ? "text-emerald-700" : "text-red-600";
  const text = money ? formatCurrency(Math.abs(value)) : `${Math.abs(value)}${suffix}`;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${cls}`}>
      <Icon className="w-3.5 h-3.5" />{flat ? "flat" : `${value >= 0 ? "+" : "−"}${text}`}
    </span>
  );
}

function Tile({ label, value, dir }) {
  const cls = dir === "up" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : dir === "down" ? "bg-red-50 border-red-200 text-red-800" : "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div className={`rounded-lg p-3 border ${cls}`}>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
